/**
 * EQ BAV Video Banner — lazy video controller.
 *
 * Lives as a theme ASSET (not an inline <script>) on purpose:
 *   1. Inline scripts do NOT execute when the theme editor re-renders a section
 *      via innerHTML — so an inline version silently dies every time Hande edits
 *      a setting, which is exactly how this bug was found.
 *   2. An external asset is CSP-safe and cacheable.
 *
 * Contract per banner:
 *   - No video bytes until the banner is near the viewport (IntersectionObserver).
 *   - Phones get the small mp4 rendition, desktop the large one.
 *   - prefers-reduced-motion: never load the video; the poster is the design.
 *   - Pause when off-screen or the tab is hidden; resume on return.
 */
(function () {
  'use strict';

  var SELECTOR = '.eq-bav-video-banner';
  var READY_ATTR = 'data-eqb-ready';

  function prefersReducedMotion() {
    return !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  }

  function pickSource(video) {
    var small = video.getAttribute('data-src-small');
    var large = video.getAttribute('data-src-large');
    var narrow = window.matchMedia('(max-width: 749px)').matches;
    var lowDensity = (window.devicePixelRatio || 1) < 1.5 && window.innerWidth < 1100;
    return ((narrow || lowDensity) && small) ? small : (large || small);
  }

  function init(root) {
    if (!root || root.getAttribute(READY_ATTR) === '1') return;
    var video = root.querySelector('.eqb-video');
    if (!video) return;
    root.setAttribute(READY_ATTR, '1');

    // Reduced motion: the poster stands in, nothing is fetched.
    if (prefersReducedMotion()) return;

    var loaded = false;

    function load() {
      if (loaded) return;
      var src = pickSource(video);
      if (!src) return;
      loaded = true;
      video.muted = true; // Safari autoplay guard
      video.setAttribute('muted', '');
      video.src = src;
      video.addEventListener('playing', function () {
        video.classList.add('is-playing');
      }, { once: true });
      var p = video.play();
      if (p && typeof p.catch === 'function') {
        p.catch(function () {
          // Autoplay refused (rare with muted+playsinline). Poster remains; show it as the frame.
          video.classList.remove('is-playing');
        });
      }
    }

    function inViewport(margin) {
      var r = root.getBoundingClientRect();
      var vh = window.innerHeight || document.documentElement.clientHeight;
      return r.bottom > -margin && r.top < vh + margin;
    }

    if ('IntersectionObserver' in window) {
      var loader = new IntersectionObserver(function (entries) {
        for (var i = 0; i < entries.length; i++) {
          if (entries[i].isIntersecting) { load(); loader.disconnect(); break; }
        }
      }, { rootMargin: '250px 0px' });
      loader.observe(root);

      // Give CPU/battery back when it's off-screen.
      var pauser = new IntersectionObserver(function (entries) {
        for (var j = 0; j < entries.length; j++) {
          if (!loaded) continue;
          if (entries[j].isIntersecting) { video.play().catch(function () {}); }
          else { video.pause(); }
        }
      }, { threshold: 0.01 });
      pauser.observe(root);
    } else {
      load();
    }

    // Belt and braces: if the banner is already on screen at init (or an observer
    // never fires because the section was injected post-load), load on the next tick.
    if (inViewport(250)) { setTimeout(load, 60); }

    // And a one-shot scroll fallback, in case observers are throttled in a preview iframe.
    var onScroll = function () {
      if (loaded) { window.removeEventListener('scroll', onScroll); return; }
      if (inViewport(250)) { load(); window.removeEventListener('scroll', onScroll); }
    };
    window.addEventListener('scroll', onScroll, { passive: true });

    document.addEventListener('visibilitychange', function () {
      if (!loaded) return;
      if (document.hidden) { video.pause(); }
      else { video.play().catch(function () {}); }
    });
  }

  function initAll(scope) {
    var nodes = (scope || document).querySelectorAll(SELECTOR);
    for (var i = 0; i < nodes.length; i++) init(nodes[i]);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(); });
  } else {
    initAll();
  }

  // Theme editor: re-init when this section is re-rendered or reordered.
  document.addEventListener('shopify:section:load', function (e) {
    initAll(e.target || document);
  });
  document.addEventListener('shopify:section:reorder', function () { initAll(); });
})();
